import React, { useState } from 'react';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Home,
  LockKeyhole,
  UnlockKeyhole,
  GripVertical,
  Check,
  Circle,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { TutorialEngine } from '@/components/tutorial/TutorialEngine';
import { cn } from '@/lib/utils';

const WEEK_NAMES = [
  'Domingo',
  'Lunes',
  'Martes',
  'Miércoles',
  'Jueves',
  'Viernes',
  'Sábado',
];

// Índice por date.getDay(): 0 = Domingo -> 'D', 1 = Lunes -> 'L', ...
const DAY_LETTERS = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];

const TODAY_CLIENTS = ['LARA', 'PEPE', 'LOLA'];
const TARGET_DAY_CLIENTS = ['LARA'];

const STEPS = [
  { id: 'day-action', target: 'day-action', message: 'Pulsa otro día para ver los clientes de esa jornada.' },
  { id: 'day-result', target: 'day-result', message: 'Cada día muestra únicamente los clientes asignados a esa jornada.' },
  { id: 'back-today-chip', target: 'back-today-chip', message: 'Pulsa el día de hoy para volver a tu ruta actual.' },
  { id: 'week-next', target: 'week-next', message: 'Usa esta flecha para avanzar una semana.' },
  { id: 'week-result', target: 'week-result', message: 'Has avanzado una semana manteniendo el mismo día seleccionado.' },
  { id: 'week-prev', target: 'week-prev', message: 'Pulsa aquí para volver a la semana anterior.' },
  { id: 'calendar-open', target: 'calendar-open', message: 'Pulsa el calendario para elegir una fecha concreta.' },
  { id: 'calendar-select', target: 'calendar-select', message: 'Selecciona esta fecha.' },
  { id: 'home-action', target: 'home-action', message: 'Pulsa Home para volver directamente al día de hoy.' },
  { id: 'home-result', target: 'home-result', message: 'Perfecto. Home te devuelve siempre al día de hoy.' },
  { id: 'mark-all', target: 'mark-all', message: 'Pulsa aquí para marcar todos los clientes del día.' },
  { id: 'mark-all-result', target: 'mark-all-result', message: 'Todos los clientes del día quedan marcados de una sola vez.' },
  { id: 'unmark-all', target: 'unmark-all', message: 'Pulsa de nuevo para desmarcarlos todos.' },
  { id: 'unmark-all-result', target: 'unmark-all-result', message: 'Perfecto. Todos los clientes vuelven a quedar sin marcar.' },
  { id: 'unlock-reorder', target: 'unlock-reorder', message: 'Pulsa el candado para desbloquear el orden de los clientes.' },
  { id: 'reorder-action', target: 'reorder-action', message: 'Arrastra a LOLA hasta la primera posición.' },
  { id: 'reorder-scope', target: 'reorder-scope', message: '' },
  { id: 'reorder-result', target: 'reorder-result', message: 'El nuevo orden se ha aplicado solo a este día.' },
  { id: 'lock-reorder', target: 'lock-reorder', message: 'Pulsa de nuevo el candado para bloquear el orden.' },
  { id: 'tutorial-complete', target: 'tutorial-complete', message: '' },
];

function formatDateKey(date) {
  const yyyy = String(date.getFullYear());
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function formatDateLabel(date) {
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yy = String(date.getFullYear()).slice(-2);
  return `${WEEK_NAMES[date.getDay()]} · ${dd}/${mm}/${yy}`;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isSameMonth(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

function mondayOf(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

function getWeekDates(anchor) {
  const monday = mondayOf(anchor);
  const dates = [];
  for (let i = 0; i < 7; i++) {
    dates.push(addDays(monday, i));
  }
  return dates;
}

function buildToday() {
  const d = new Date();
  return {
    date: d,
    dateKey: formatDateKey(d),
    name: WEEK_NAMES[d.getDay()],
  };
}

function CoachMark({ text, className }) {
  return (
    <div
      className={cn(
        'max-w-[260px] rounded-md border border-primary/40 bg-primary/10 px-2 py-1.5 text-xs font-medium text-foreground shadow-sm',
        className
      )}
    >
      {text}
    </div>
  );
}

function SortableClientRow({ name, isCompleted, canDrag, isHighlighted }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: name });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 rounded-md border px-3 py-2',
        isCompleted ? 'border-primary bg-primary/40' : 'border-border bg-card',
        isHighlighted ? 'ring-2 ring-primary ring-offset-1' : ''
      )}
    >
      <span
        {...(canDrag ? attributes : {})}
        {...(canDrag ? listeners : {})}
        style={canDrag ? { touchAction: 'none' } : undefined}
        className={cn(
          'flex shrink-0 items-center justify-center',
          canDrag ? 'cursor-grab text-primary' : 'text-muted-foreground'
        )}
      >
        <GripVertical className="h-4 w-4" />
      </span>
      <span className="text-sm font-medium">{name}</span>
    </div>
  );
}

function SimulatedScreen({
  activeTarget,
  message,
  today,
  currentDate,
  weekDates,
  displayClients,
  targetDate,
  calendarTargetDate,
  calendarOpen,
  clientsFor,
  completedDogs,
  allMarked,
  reorderUnlocked,
  scopeDialogOpen,
  pendingDogName,
  onSelectChip,
  onWeekNext,
  onWeekPrev,
  onOpenCalendar,
  onCalendarSelect,
  onHome,
  onToggleMarkAll,
  onToggleLock,
  onDragEnd,
  onApplyDayScope,
  onContinue,
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  const isDayAction = activeTarget === 'day-action';
  const isBackToday = activeTarget === 'back-today-chip';
  const chipsActive = isDayAction || isBackToday;

  const isWeekNext = activeTarget === 'week-next';
  const isWeekPrev = activeTarget === 'week-prev';

  const isCalendarOpenStep = activeTarget === 'calendar-open';
  const isCalendarSelect = activeTarget === 'calendar-select';
  const isHomeAction = activeTarget === 'home-action';

  const isDayResult = activeTarget === 'day-result';
  const isWeekResult = activeTarget === 'week-result';
  const isHomeResult = activeTarget === 'home-result';

  const isMarkAll = activeTarget === 'mark-all';
  const isUnmarkAll = activeTarget === 'unmark-all';
  const isMarkAllResult = activeTarget === 'mark-all-result';
  const isUnmarkAllResult = activeTarget === 'unmark-all-result';

  const isUnlockReorder = activeTarget === 'unlock-reorder';
  const isReorderAction = activeTarget === 'reorder-action';
  const isReorderResult = activeTarget === 'reorder-result';
  const isLockReorder = activeTarget === 'lock-reorder';

  const dayName = WEEK_NAMES[currentDate.getDay()];

  return (
    <div className="overflow-hidden rounded-lg border bg-background">
      {/* Cabecera */}
      <div className="flex items-center justify-between border-b px-3 py-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-200">
          <CalendarDays className="h-3.5 w-3.5" />
          Entorno de práctica
        </span>
        <span className="text-xs font-medium text-muted-foreground">{dayName}</span>
      </div>

      {/* Fecha con flechas de semana */}
      <div className="px-3 pt-2">
        {(isWeekNext || isWeekPrev) && (
          <div className="mb-2 flex justify-center">
            <CoachMark text={message} />
          </div>
        )}
        <div className="flex items-center justify-center gap-4 py-1">
          <button
            type="button"
            onClick={onWeekPrev}
            aria-label="Semana anterior"
            className={cn(
              'flex h-9 w-9 items-center justify-center rounded-full border border-border text-primary',
              isWeekPrev ? 'bg-primary/10 ring-2 ring-primary ring-offset-1' : '',
              isWeekNext ? 'opacity-40' : ''
            )}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <p className="text-sm font-semibold text-foreground">{formatDateLabel(currentDate)}</p>
          <button
            type="button"
            onClick={onWeekNext}
            aria-label="Semana siguiente"
            className={cn(
              'flex h-9 w-9 items-center justify-center rounded-full border border-border text-primary',
              isWeekNext ? 'bg-primary/10 ring-2 ring-primary ring-offset-1' : '',
              isWeekPrev ? 'opacity-40' : ''
            )}
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Chips de días (lunes -> domingo) */}
      <div className="px-3 pb-2 pt-2">
        {(isDayAction || isBackToday) && (
          <div className="mb-2 flex justify-center">
            <CoachMark text={message} />
          </div>
        )}
        <div className="grid grid-cols-7 gap-1">
          {weekDates.map((date) => {
            const isSelected = isSameDay(date, currentDate);
            const isTarget = isDayAction && isSameDay(date, targetDate);
            const isTodayChip = isBackToday && isSameDay(date, today.date);
            const isHighlighted = isTarget || isTodayChip;
            const isInert = chipsActive && !isHighlighted;
            const count = clientsFor(date).length;

            return (
              <button
                key={formatDateKey(date)}
                type="button"
                onClick={() => onSelectChip(date)}
                disabled={isInert}
                className={cn(
                  'flex min-w-0 flex-col items-center rounded-md border px-0.5 py-1 text-foreground',
                  isSelected ? 'bg-primary/40 border-border' : 'bg-card border-border',
                  isHighlighted ? 'ring-2 ring-primary ring-offset-1' : '',
                  isInert ? 'opacity-40' : ''
                )}
              >
                <span className="text-xs uppercase">{DAY_LETTERS[date.getDay()]}</span>
                <span className="text-base font-bold">{date.getDate()}</span>
                <span className="text-xs text-muted-foreground">({count})</span>
              </button>
            );
          })}
        </div>
      </div>


      {/* Barra de acciones */}
      <div className="px-3 pb-3">
        {(isCalendarOpenStep || isHomeAction || isMarkAll || isUnmarkAll || isUnlockReorder || isLockReorder) && (
          <div className="mb-2 flex justify-center">
            <CoachMark text={message} />
          </div>
        )}
        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={onToggleLock}
            disabled={!isUnlockReorder && !isLockReorder}
            aria-label={reorderUnlocked ? 'Bloquear reordenación' : 'Desbloquear reordenación'}
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-full border bg-muted border-primary',
              isUnlockReorder || isLockReorder ? 'ring-2 ring-primary ring-offset-2' : 'opacity-40'
            )}
          >
            {reorderUnlocked ? (
              <UnlockKeyhole className="h-5 w-5 text-primary" />
            ) : (
              <LockKeyhole className="h-5 w-5 text-primary" />
            )}
          </button>

          <button
            type="button"
            onClick={onHome}
            aria-label="Hoy"
            className={cn(
              'flex h-12 w-12 items-center justify-center rounded-full border bg-muted border-primary shadow-md',
              isHomeAction ? 'ring-2 ring-primary ring-offset-2' : '',
              isCalendarOpenStep ? 'opacity-40' : ''
            )}
          >
            <Home className="h-6 w-6 text-primary" />
          </button>

          <button
            type="button"
            onClick={onOpenCalendar}
            aria-label="Calendario"
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-full border bg-muted border-primary',
              isCalendarOpenStep ? 'ring-2 ring-primary ring-offset-2' : '',
              isHomeAction ? 'opacity-40' : ''
            )}
          >
            <CalendarDays className="h-5 w-5 text-primary" />
          </button>

          <button
            type="button"
            onClick={onToggleMarkAll}
            disabled={!isMarkAll && !isUnmarkAll}
            aria-label={allMarked ? 'Desmarcar todo' : 'Marcar todo'}
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-full border bg-muted border-primary',
              isMarkAll || isUnmarkAll ? 'ring-2 ring-primary ring-offset-2' : 'opacity-40'
            )}
          >
            {allMarked ? (
              <Circle className="h-5 w-5 text-primary" />
            ) : (
              <Check className="h-5 w-5 text-primary" />
            )}
          </button>
        </div>
      </div>

      {/* Calendario simulado */}
      {calendarOpen && (
        <div className="border-t px-3 py-3">
          {isCalendarSelect && (
            <div className="mb-2 flex justify-center">
              <CoachMark text={message} />
            </div>
          )}
          <div className="flex justify-center rounded-md border bg-card p-1">
            <Calendar
              mode="single"
              onSelect={(date) => {
                if (date) onCalendarSelect(date);
              }}
              defaultMonth={calendarTargetDate}
              disabled={(date) => !isSameDay(date, calendarTargetDate)}
              modifiers={{ target: calendarTargetDate }}
              modifiersClassNames={{ target: 'ring-2 ring-primary' }}
            />
          </div>
        </div>
      )}

      {/* Lista de clientes del día */}
      <div className="min-h-32 border-t px-3 py-3">
        {(isDayResult || isWeekResult || isHomeResult || isMarkAllResult || isUnmarkAllResult || isReorderAction || isReorderResult) && (
          <div className="mb-2 flex justify-center">
            <CoachMark text={message} />
          </div>
        )}

        {scopeDialogOpen && (
          <div className="mb-3 rounded-md border bg-card p-4">
            <h3 className="text-sm font-semibold">
              ¿Cómo quieres aplicar el cambio de {pendingDogName}?
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Elige si este nuevo orden será puntual o permanente.
            </p>
            <div className="mt-3 flex gap-2">
              <Button type="button" size="sm" onClick={onApplyDayScope}>
                Solo este día
              </Button>
              <Button type="button" size="sm" variant="outline" disabled>
                Desde este día en adelante
              </Button>
            </div>
          </div>
        )}

        {displayClients.length === 0 ? (
          <p className="mt-8 text-center text-sm text-muted-foreground">
            No hay clientes para este día.
          </p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
          >
            <SortableContext items={displayClients} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {displayClients.map((name) => {
                  const isCompleted = completedDogs.has(name);
                  const canDrag = isReorderAction && reorderUnlocked && name === 'LOLA';
                  const isHighlighted = isReorderAction && name === 'LOLA';
                  const dimmed = isReorderAction && name !== 'LOLA';
                  return (
                    <div key={name} className={dimmed ? 'opacity-40' : ''}>
                      <SortableClientRow
                        name={name}
                        isCompleted={isCompleted}
                        canDrag={canDrag}
                        isHighlighted={isHighlighted}
                      />
                    </div>
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        )}

        {(isDayResult || isWeekResult || isHomeResult || isMarkAllResult || isUnmarkAllResult || isReorderResult) && (
          <div className="mt-3 flex justify-center">
            <Button type="button" size="sm" onClick={onContinue}>
              Continuar
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function SimulatedComplete({ onRestartTutorial, onExitTutorial, exitLabel = 'Volver a Tutoriales' }) {
  return (
    <div className="rounded-lg border bg-background p-6 text-center">
      <h3 className="text-lg font-semibold">Has completado Navegación y gestión diaria.</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Ya sabes moverte por tu ruta y gestionar las acciones del día.
      </p>
      <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
        <Button type="button" variant="outline" onClick={onRestartTutorial}>
          Repetir tutorial
        </Button>
        <Button type="button" onClick={onExitTutorial}>
          {exitLabel}
        </Button>
      </div>
    </div>
  );
}

export function DailyNavigationTutorial({ onExit, completionActionLabel, onCompletionAction }) {
  const [today] = useState(() => buildToday());
  const [currentDate, setCurrentDate] = useState(() => new Date(today.date));
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [completedDogs, setCompletedDogs] = useState(() => new Set());
  const [reorderUnlocked, setReorderUnlocked] = useState(false);
  const [order, setOrder] = useState(() => [...TODAY_CLIENTS]);
  const [pendingOrder, setPendingOrder] = useState(null);
  const [pendingDogName, setPendingDogName] = useState(null);
  const [scopeDialogOpen, setScopeDialogOpen] = useState(false);

  // Día objetivo distinto de hoy, siempre dentro de la misma semana.
  const targetDayName = today.name === 'Lunes' ? 'Martes' : 'Lunes';

  // Fecha concreta del calendario, siempre en el mismo mes de hoy.
  const tomorrow = addDays(today.date, 1);
  const calendarTargetDate = isSameMonth(tomorrow, today.date)
    ? tomorrow
    : addDays(today.date, -1);

  const weekDates = getWeekDates(currentDate);
  const targetDate = weekDates.find(
    (date) => WEEK_NAMES[date.getDay()] === targetDayName
  );

  const clientsFor = (date) => {
    if (isSameDay(date, today.date)) return TODAY_CLIENTS;
    if (WEEK_NAMES[date.getDay()] === targetDayName) return TARGET_DAY_CLIENTS;
    return [];
  };

  const currentClients = clientsFor(currentDate);
  const allMarked =
    currentClients.length > 0 &&
    currentClients.every((name) => completedDogs.has(name));
  const displayClients = isSameDay(currentDate, today.date) ? order : currentClients;

  const resetScenario = () => {
    setCurrentDate(new Date(today.date));
    setCalendarOpen(false);
    setCompletedDogs(new Set());
    setReorderUnlocked(false);
    setOrder([...TODAY_CLIENTS]);
    setPendingOrder(null);
    setPendingDogName(null);
    setScopeDialogOpen(false);
  };

  const handleBack = (targetStep) => {
    if (!targetStep) return;

    switch (targetStep.id) {
      case 'day-action':
        setCurrentDate(new Date(today.date));
        break;
      case 'day-result':
        setCurrentDate(new Date(targetDate));
        break;
      case 'back-today-chip':
        setCurrentDate(new Date(targetDate));
        break;
      case 'week-next':
        setCurrentDate(new Date(today.date));
        break;
      case 'week-result':
        setCurrentDate(addDays(today.date, 7));
        break;
      case 'week-prev':
        setCurrentDate(addDays(today.date, 7));
        break;
      case 'calendar-open':
        setCurrentDate(new Date(today.date));
        setCalendarOpen(false);
        break;
      case 'calendar-select':
        setCalendarOpen(true);
        setCurrentDate(new Date(today.date));
        break;
      case 'home-action':
        setCurrentDate(new Date(calendarTargetDate));
        setCalendarOpen(false);
        break;
      case 'home-result':
        setCurrentDate(new Date(today.date));
        break;
      case 'mark-all':
        setCurrentDate(new Date(today.date));
        setCompletedDogs(new Set());
        break;
      case 'mark-all-result':
        setCompletedDogs(new Set(TODAY_CLIENTS));
        break;
      case 'unmark-all':
        setCompletedDogs(new Set(TODAY_CLIENTS));
        break;
      case 'unmark-all-result':
        setCompletedDogs(new Set());
        break;
      case 'unlock-reorder':
        setReorderUnlocked(false);
        break;
      case 'reorder-action':
        setReorderUnlocked(true);
        setOrder([...TODAY_CLIENTS]);
        setScopeDialogOpen(false);
        setPendingOrder(null);
        setPendingDogName(null);
        break;
      case 'reorder-scope':
        setScopeDialogOpen(true);
        setPendingOrder(arrayMove([...TODAY_CLIENTS], TODAY_CLIENTS.indexOf('LOLA'), 0));
        setPendingDogName('LOLA');
        break;
      case 'reorder-result':
        setReorderUnlocked(true);
        break;
      case 'lock-reorder':
        setReorderUnlocked(true);
        break;
      default:
        break;
    }
  };

  const renderStep = (step, index, actions) => {
    const goNext = actions?.goNext ?? (() => {});
    const restart = actions?.restart ?? (() => {});
    const exit = actions?.exit ?? (() => {});
    const completeAction = onCompletionAction ?? exit;
    const completeLabel = completionActionLabel ?? 'Volver a Tutoriales';

    const handleSelectChip = (date) => {
      if (step.id === 'day-action' && isSameDay(date, targetDate)) {
        setCurrentDate(date);
        goNext();
      } else if (step.id === 'back-today-chip' && isSameDay(date, today.date)) {
        setCurrentDate(new Date(today.date));
        goNext();
      }
    };

    const handleWeekNext = () => {
      if (step.id === 'week-next') {
        setCurrentDate(addDays(currentDate, 7));
        goNext();
      }
    };

    const handleWeekPrev = () => {
      if (step.id === 'week-prev') {
        setCurrentDate(addDays(currentDate, -7));
        goNext();
      }
    };

    const handleOpenCalendar = () => {
      if (step.id === 'calendar-open') {
        setCalendarOpen(true);
        goNext();
      }
    };

    const handleCalendarSelect = (date) => {
      if (step.id === 'calendar-select' && isSameDay(date, calendarTargetDate)) {
        setCurrentDate(date);
        setCalendarOpen(false);
        goNext();
      }
    };

    const handleHome = () => {
      if (step.id === 'home-action') {
        setCurrentDate(new Date(today.date));
        goNext();
      }
    };

    const handleContinue = () => {
      goNext();
    };

    const handleToggleMarkAll = () => {
      if (step.id === 'mark-all') {
        setCompletedDogs(new Set(currentClients));
        goNext();
      } else if (step.id === 'unmark-all') {
        setCompletedDogs(new Set());
        goNext();
      }
    };

    const handleToggleLock = () => {
      if (step.id === 'unlock-reorder') {
        setReorderUnlocked(true);
        goNext();
      } else if (step.id === 'lock-reorder') {
        setReorderUnlocked(false);
        goNext();
      }
    };

    const handleDragEnd = (event) => {
      const { active, over } = event;
      if (
        step.id !== 'reorder-action' ||
        !reorderUnlocked ||
        !over ||
        active.id === over.id ||
        active.id !== 'LOLA'
      ) {
        return;
      }
      const oldIndex = displayClients.indexOf(active.id);
      const newIndex = displayClients.indexOf(over.id);
      const candidateOrder = arrayMove(displayClients, oldIndex, newIndex);
      if (candidateOrder[0] !== 'LOLA') {
        return;
      }
      setPendingOrder(candidateOrder);
      setPendingDogName('LOLA');
      setScopeDialogOpen(true);
      goNext();
    };

    const handleApplyDayScope = () => {
      if (step.id === 'reorder-scope' && pendingOrder) {
        setOrder(pendingOrder);
        setScopeDialogOpen(false);
        setPendingOrder(null);
        setPendingDogName(null);
        goNext();
      }
    };

    if (step.id === 'tutorial-complete') {
      return (
        <SimulatedComplete
          onRestartTutorial={restart}
          onExitTutorial={completeAction}
          exitLabel={completeLabel}
        />
      );
    }

    return (
      <SimulatedScreen
        activeTarget={step.target}
        message={step.message}
        today={today}
        currentDate={currentDate}
        weekDates={weekDates}
        displayClients={displayClients}
        targetDate={targetDate}
        calendarTargetDate={calendarTargetDate}
        calendarOpen={calendarOpen}
        clientsFor={clientsFor}
        completedDogs={completedDogs}
        allMarked={allMarked}
        reorderUnlocked={reorderUnlocked}
        scopeDialogOpen={scopeDialogOpen}
        pendingDogName={pendingDogName}
        onSelectChip={handleSelectChip}
        onWeekNext={handleWeekNext}
        onWeekPrev={handleWeekPrev}
        onOpenCalendar={handleOpenCalendar}
        onCalendarSelect={handleCalendarSelect}
        onHome={handleHome}
        onToggleMarkAll={handleToggleMarkAll}
        onToggleLock={handleToggleLock}
        onDragEnd={handleDragEnd}
        onApplyDayScope={handleApplyDayScope}
        onContinue={handleContinue}
      />
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <DialogHeader>
        <DialogTitle>Navegación y gestión diaria</DialogTitle>
        <DialogDescription>
          Aprende a moverte entre días y semanas en un entorno de práctica.
        </DialogDescription>
      </DialogHeader>

      <TutorialEngine
        steps={STEPS}
        renderStep={renderStep}
        onExit={onExit}
        onRestart={resetScenario}
        onBack={handleBack}
        showStepNavigation={false}
      />
    </div>
  );
}
